import { useState } from 'react';
import { useSWRConfig } from 'swr';

import myShopClient from '@services/nearlive';
import { useVerifyEmail } from '@/components/verification/VerifyEmailProvider';

import { activateItemOptimistically, deactivateItemOptimistically } from './useInvalidateLocalPotential';

/**
 * Channel enable / disable / register-interest actions for a shop.
 *
 * Mirrors the retailers-dashboard `@services/channels` pattern (POST to the
 * per-shop channel endpoints) but calls the API directly through
 * `myShopClient` so unify doesn't pull in the shared service's `ShopProvider`
 * / `@nearst/ui` dependency chain.
 *
 * Each action revalidates the `useChannels` SWR cache (`['unify:channels',
 * shopId]`) so the page reflects the new status without a manual refresh.
 * In-flight channel types are tracked in a Set, so `isBusy(type)` lets each
 * button independently show a spinner even if two actions overlap.
 *
 * **Neither enable nor disable POSTs the billing endpoint.** Both legacy
 * endpoints publish `shop.channel.enabled` / `.disabled`, and unify-billing's
 * `on-channel-enabled` (NEA-6101) and `on-channel-disabled` (NEA-5605) do the
 * Stripe write off those events.
 *
 * Firing the endpoint alongside put two writers on the same
 * `stripe.subscriptions.update`, with the same idempotency key, milliseconds
 * apart. The subscriber won in ~1.6s; the direct call hung until its Lambda
 * timed out at 29s, giving the retailer a long spinner and a "contact support,
 * your billing is wrong" warning on a correctly-billed subscription. It
 * happened on every paid feed-channel enable from mid-July (3 of 3) before
 * NEA-6421 removed the enable call; NEA-5605 removed the disable one before it
 * could do the same, when it made the subscriber the first real remover.
 * Short-circuits can't prevent it: mid-race the channels row has moved but the
 * Stripe item hasn't, so the direct call proceeds to the same update.
 *
 * The trade is that the Stripe write is async-only on both toggles. It is
 * monitored — each subscriber dead-letters to its own queue with a P1 depth
 * alarm (`ChannelBillingDlq` / `ChannelDisableBillingDlq`).
 */
export function useChannelActions(shopId) {
	const { mutate } = useSWRConfig();
	const { requireVerifiedEmail } = useVerifyEmail();
	const [busyTypes, setBusyTypes] = useState(() => new Set());
	const [error, setError] = useState(null);

	// The add-on lands on (or leaves) the subscription a second or two after the
	// SNS event, so an immediate revalidation can still read the pre-toggle
	// entitlements (channel prices, the Pro allowance). Revalidate again once the
	// async biller has had time to land — the Channels page stays mounted, so
	// without this it would show stale pricing until the next focus/mount.
	const revalidateBillingAfterAsyncWrite = () => {
		mutate(['billing-status', shopId]);
		setTimeout(() => mutate(['billing-status', shopId]), 5000);
	};

	const run = async (type, path) => {
		setBusyTypes((prev) => new Set(prev).add(type));
		setError(null);
		try {
			const client = await myShopClient();
			const { data } = await client.post(`/api/channels/${shopId}/${type}/${path}`);
			const togglesScore = path === 'enable' || path === 'disable';
			if (togglesScore) {
				revalidateBillingAfterAsyncWrite();
				// Optimistic Local Potential gauge bump — the resolver returns per-item
				// uplifts in `groups[]`, so we predict the new score and the gauge moves
				// on click; the revalidation below settles it on the server value.
				const updater = path === 'enable' ? activateItemOptimistically : deactivateItemOptimistically;
				await mutate(['local-potential', shopId], (current) => updater(current, type), { revalidate: false });
			}
			await Promise.all([mutate(['unify:channels', shopId]), ...(togglesScore ? [mutate(['local-potential', shopId])] : [])]);
			return data;
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setBusyTypes((prev) => {
				const next = new Set(prev);
				next.delete(type);
				return next;
			});
		}
	};

	// Wizard channels (Meta, Reservations, Local Checkout) need to seed the
	// channel record with a `pending` status and a config body before the user
	// finishes the setup flow — this is what the create handler at
	// POST /api/channels/{shopId}/{type} expects (a body, no /enable suffix).
	const create = async (type, body) => {
		setBusyTypes((prev) => new Set(prev).add(type));
		setError(null);
		try {
			const client = await myShopClient();
			const { data } = await client.post(`/api/channels/${shopId}/${type}`, body);
			await mutate(['unify:channels', shopId]);
			return data;
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setBusyTypes((prev) => {
				const next = new Set(prev);
				next.delete(type);
				return next;
			});
		}
	};

	// Wizard-terminal enable (NEA-6101). Used by the Product Reservations
	// wizard which needs to (a) persist the retailer's config (notification
	// emails) alongside (b) fire the standard enable pipe (channels-{stage}
	// write + `shop.channel.enabled` on SNS, which drives `toggleStockChannels`
	// + the GMC orders feed).
	//
	// Two sequential calls:
	//   1. POST /api/channels/{shopId}/{type}  — write config, seed as pending
	//   2. POST /api/channels/{shopId}/{type}/enable  — standard enable path
	//      (`channels/enable.js` publishes `shop.channel.enabled`, which is
	//      what wires stock flags on Kinesis + creates the GMC orders feed
	//      for PR/LC, and is what `unify-billing/on-channel-enabled` bills
	//      off). Rahul's PR review — before this, wizardEnable wrote
	//      status=`enabled` directly via the create endpoint, which skipped
	//      the SNS pipe, so PR was billed and shown live but its stock was
	//      never actually flagged for the feed.
	//
	// There used to be a third call — the billing endpoint, for synchronous
	// failure surfacing. It raced step 2's SNS subscriber on the same Stripe
	// update, which is what NEA-6421 removed; see the header comment.
	//
	// Gated on verified email — the wizard is a "go-live" action just like
	// the regular `enable`.
	const wizardEnableInternal = async (type, body) => {
		setBusyTypes((prev) => new Set(prev).add(type));
		setError(null);
		try {
			const client = await myShopClient();
			await client.post(`/api/channels/${shopId}/${type}`, { ...body, status: 'pending' });
			await client.post(`/api/channels/${shopId}/${type}/enable`);
			revalidateBillingAfterAsyncWrite();
			await mutate(['local-potential', shopId], (current) => activateItemOptimistically(current, type), { revalidate: false });
			await Promise.all([mutate(['unify:channels', shopId]), mutate(['local-potential', shopId])]);
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setBusyTypes((prev) => {
				const next = new Set(prev);
				next.delete(type);
				return next;
			});
		}
	};

	// Patch a single channel's config without changing status — used by the
	// Meta wizard to store the domain verification code between steps.
	const update = async (type, config) => {
		setBusyTypes((prev) => new Set(prev).add(type));
		setError(null);
		try {
			const client = await myShopClient();
			const { data } = await client.patch(`/api/channels/${shopId}/${type}/update`, config);
			await mutate(['unify:channels', shopId]);
			return data;
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setBusyTypes((prev) => {
				const next = new Set(prev);
				next.delete(type);
				return next;
			});
		}
	};

	return {
		// Going live on any channel requires a verified email (NEA-5773) — gate
		// the canonical enable action so every "make it live" path (storefront,
		// channel detail, the Meta wizard's final step) is covered, not just the
		// "go live" CTA. Unverified → opens the verify dialog and runs the enable
		// on success; verified → enables immediately (promise passed through).
		enable: (type) => requireVerifiedEmail(() => run(type, 'enable')),
		disable: (type) => run(type, 'disable'),
		registerInterest: (type) => run(type, 'register-interest'),
		create,
		// NEA-6101 — wizard-terminal enable: persist config + fire billing in
		// one call, gated on verified email (same as the regular enable).
		wizardEnable: (type, body) => requireVerifiedEmail(() => wizardEnableInternal(type, body)),
		update,
		isBusy: (type) => busyTypes.has(type),
		error
	};
}
